<!--
  - Copyright (c) 2019 brightlamp
  -->

<template>
    <div>
        <fullscreen :id="elementId" :style="`position:relative;height:${calculatedHeight}`" ref="fullscreen" @change="fullscreenChange" background="#ffffff">
            <LineChart :styles="chartStyles" :chart-data="chartData.data" :options="chartData.options"/>
            <button v-if="!saving" type="button" class="btn btn-default btn-map-fullscreen" @click="toggleFullScreen">
                <i class="mdi" :class="[fullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen']"/>
            </button>
            <v-tooltip bottom color="red darken-4" open-delay="200">
                <template #activator="{ on }">
                    <span v-on="on">
                        <v-chip v-if="testsAndParameters.minNormalData" label :default="fullscreen" :x-small="!fullscreen" :class="fullscreen ? 'mx-1 my-1 red darken-4' : 'mx-1 red darken-4'" text-color="white">{{`Normative Range | ${testsAndParameters.minNormalData} - ${testsAndParameters.maxNormalData}`}}</v-chip>
                        <v-chip v-else label :default="fullscreen" :x-small="!fullscreen" :class="fullscreen ? 'mx-1 my-1 red darken-4' : 'mx-1 red darken-4'" text-color="white">{{"Normative Range | N/A"}}</v-chip>
                    </span>
                </template>
                <span v-if="testsAndParameters.minNormalData">{{`Normative Range | ${testsAndParameters.minNormalData} - ${testsAndParameters.maxNormalData}`}}</span>
                <span v-else>{{"Normative Range | N/A"}}</span>
            </v-tooltip>
            <template v-if="eyeLocation === 'Right'">
                <template v-if="testsAndParameters.rightBaseline">
                    <v-tooltip bottom color="blue darken-4" open-delay="200">
                        <template #activator="{ on }">
                            <span v-on="on">
                                <v-chip label :default="fullscreen" :x-small="!fullscreen" :class="fullscreen ? 'mx-1 my-1 blue darken-4' : 'mx-1 blue darken-4'" text-color="white">{{`Baseline | ${testsAndParameters.rightBaseline.item} | ${testsAndParameters.rightBaseline.testDate}`}}</v-chip>
                            </span>
                        </template>
                        <span>{{`Baseline | ${testsAndParameters.rightBaseline.item} | ${testsAndParameters.rightBaseline.testDate}`}}</span>
                    </v-tooltip>
                </template>
                <template v-for="item in testsAndParameters.rightItems">
                    <v-tooltip bottom color="orange accent-4" open-delay="200">
                        <template #activator="{ on }">
                            <span v-on="on">
                                <v-chip label :default="fullscreen" :x-small="!fullscreen" :class="fullscreen ? 'mx-1 my-1 orange accent-4' : 'mx-1 orange accent-4'" text-color="white">{{`T${item.index} | ${item.item} | ${item.testDate}`}}</v-chip>
                            </span>
                        </template>
                        <span>{{`T${item.index} | ${item.item} | ${item.testDate}`}}</span>
                    </v-tooltip>
                </template>
            </template>
            <template v-if="eyeLocation === 'Left' || testsAndParameters.showTogether">
                <template v-if="testsAndParameters.leftBaseline">
                    <v-tooltip bottom color="deep-purple accent-2" open-delay="200">
                        <template #activator="{ on }">
                            <span v-on="on">
                                <v-chip label :default="fullscreen" :x-small="!fullscreen" :class="fullscreen ? 'mx-1 my-1 deep-purple accent-2' : 'mx-1 deep-purple accent-2'" text-color="white">{{`Baseline | ${testsAndParameters.leftBaseline.item} | ${testsAndParameters.leftBaseline.testDate}`}}</v-chip>
                            </span>
                        </template>
                        <span>{{`Baseline | ${testsAndParameters.leftBaseline.item} | ${testsAndParameters.leftBaseline.testDate}`}}</span>
                    </v-tooltip>
                </template>
                <template v-for="item in testsAndParameters.leftItems">
                    <v-tooltip bottom color="green darken-4" open-delay="200">
                        <template #activator="{ on }">
                                <span v-on="on">
                                    <v-chip label :default="fullscreen" :x-small="!fullscreen" :class="fullscreen ? 'mx-1 my-1 green darken-4' : 'mx-1 green darken-4'" text-color="white">{{`T${item.index} | ${item.item} | ${item.testDate}`}}</v-chip>
                                </span>
                        </template>
                        <span>{{`T${item.index} | ${item.item} | ${item.testDate}`}}</span>
                    </v-tooltip>
                </template>
            </template>
        </fullscreen>
    </div>
</template>

<script>
    import LineChart from './line-chart.js'
    import Fullscreen from 'vue-fullscreen/src/component.vue'

    export default
    {
        name: 'trending-graph',
        components:
        {
            LineChart,
            Fullscreen
        },
        data: function ()
        {
            return {
                chartData: {},
                fullscreen: false,
                originalHeight: 'auto',
                calculatedHeight: 'auto'
            }
        },
        props:
        {
            eyeLocation: String(''),
            testsAndParameters: {},
            saving: Boolean(false),
            elementId: String('')
        },
        computed:
        {
            chartStyles()
            {
                if ('auto' === this.calculatedHeight)
                {
                    return {
                        height: '90%',
                        width: '100%'
                    }
                }
                return {
                    height: `${this.calculatedHeight * .80}px`,
                    width: '100%'
                }
            }
        },
        watch:
        {
            testsAndParameters()
            {
                if (document.getElementById(this.elementId))
                {
                    this.calculatedHeight = this.originalHeight
                }
                this.chartDataCommon()
            }
        },
        mounted()
        {
            if (document.getElementById(this.elementId))
            {

                this.originalHeight = 550;
                this.calculatedHeight = this.originalHeight
            }
            this.chartDataCommon()
        },
        methods:
        {
            toggleFullScreen()
            {
                this.$refs.fullscreen.toggle()
            },
            fullscreenChange (fullscreen)
            {
                if (!fullscreen)
                {
                    this.calculatedHeight = this.originalHeight
                }
                else
                {
                    this.calculatedHeight = this.originalHeight * 2
                }
                this.fullscreen = fullscreen
            },
            chartDataCommon ()
            {
                let xData = [];
                let yRightData = [];
                let yLeftData = [];
                let yRightBaseline = [];
                let yLeftBaseline = [];
                let yMinNormalData = [];
                let yMaxNormalData = [];
                let yMeanData = [];

                const chartDataLength = 'Right' === this.eyeLocation ? this.testsAndParameters.rightItems.length : this.testsAndParameters.leftItems.length;
                for (let index = 0; index < chartDataLength; ++index)
                {
                    if (('Right' === this.eyeLocation && this.testsAndParameters.rightItems[index].item !== 'N/A') || (('Left' === this.eyeLocation || this.testsAndParameters.showTogether) && this.testsAndParameters.leftItems[index].item !== 'N/A'))
                    {
                        if ('Right' === this.eyeLocation)
                        {
                            yRightData.push(this.testsAndParameters.rightItems[index].item);
                            xData.push(this.testsAndParameters.rightItems[index].index)
                        }
                        if ('Left' === this.eyeLocation || this.testsAndParameters.showTogether)
                        {
                            yLeftData.push(this.testsAndParameters.leftItems[index].item);
                            if ('Left' === this.eyeLocation)
                            {
                                xData.push(this.testsAndParameters.leftItems[index].index)
                            }
                        }
                        this.addGuidelinePoint(yRightBaseline, yLeftBaseline, yMinNormalData, yMaxNormalData, yMeanData);
                    }
                }
                // Extend the lines in case there was only 1 data point
                this.addGuidelinePoint(yRightBaseline, yLeftBaseline, yMinNormalData, yMaxNormalData, yMeanData);

                this.chartData =
                {
                    type: 'line',
                    data:
                    {
                        labels: xData,
                        datasets: []
                    },
                    options:
                    {
                        responsive: true,
                        maintainAspectRatio: false,
                        scales:
                        {
                            yAxes:
                            [
                                {
                                    ticks:
                                    {
                                        beginAtZero: false
                                    },
                                    scaleLabel:
                                    {
                                        display: true,
                                        labelString: this.testsAndParameters.parameterLabel
                                    }
                                }
                            ],
                            xAxes:
                            [
                                {
                                    ticks:
                                    {
                                        maxTicksLimit: 30
                                    },
                                    scaleLabel:
                                    {
                                        display: true,
                                        labelString: 'Test Series'
                                    }
                                }
                            ]
                        },
                        title:
                        {
                            display: true,
                            text: `${!this.testsAndParameters.showTogether ? `${this.eyeLocation} Eye - ` : ``}${this.testsAndParameters.parameterName}`,
                            fontSize: 20
                        }
                    }
                };
                let spliceIndex = 0;
                if ('Right' === this.eyeLocation)
                {
                    if (yRightBaseline.length > 0)
                    {
                        this.chartData.data.datasets.splice(spliceIndex, 0,
                        {
                            label: `${this.eyeLocation} Baseline`,
                            data: yRightBaseline,
                            borderColor: '#0d47a1',
                            fill: false,
                            borderWidth: 1,
                            pointRadius: 0
                        });
                        spliceIndex++
                    }
                    this.chartData.data.datasets.splice(spliceIndex, 0,
                    {
                        label: `${this.eyeLocation} Eye`,
                        data: yRightData,
                        pointBackgroundColor: '#ff6d00',
                        borderColor: '#ff6d00',
                        fill: false,
                        borderWidth: 1,
                        pointRadius: 3
                    });
                    spliceIndex++
                }
                if ('Left' === this.eyeLocation || this.testsAndParameters.showTogether)
                {
                    if (yLeftBaseline.length > 0)
                    {
                        this.chartData.data.datasets.splice(spliceIndex, 0,
                        {
                            label: `Left Baseline`,
                            data: yLeftBaseline,
                            borderColor: '#7c4dff',
                            fill: false,
                            borderWidth: 1,
                            pointRadius: 0
                        })
                    }
                    this.chartData.data.datasets.splice(spliceIndex, 0,
                    {
                        label: `Left Eye`,
                        data: yLeftData,
                        pointBackgroundColor: '#1b5e20',
                        borderColor: '#1b5e20',
                        fill: false,
                        borderWidth: 1,
                        pointRadius: 3
                    })
                }
                spliceIndex++;
                // Only show if we have normative data
                if (this.testsAndParameters.minNormalData)
                {
                    this.chartData.data.datasets.splice(spliceIndex, 0,
                    {
                        label: `LCL`,
                        data: yMinNormalData,
                        borderColor: '#b71c1c',
                        fill: false,
                        borderWidth: 1,
                        pointRadius: 0
                    });
                    spliceIndex++;
                    this.chartData.data.datasets.splice(spliceIndex, 0,
                    {
                        label: `UCL`,
                        data: yMaxNormalData,
                        borderColor: '#b71c1c',
                        fill: false,
                        borderWidth: 1,
                        pointRadius: 0
                    });
                    spliceIndex++;
                    this.chartData.data.datasets.splice(spliceIndex, 0,
                    {
                        label: `CL`,
                        data: yMeanData,
                        borderColor: '#9e9e9e',
                        borderDash: [20, 5],
                        fill: false,
                        borderWidth: 1,
                        pointRadius: 0
                    })
                }
            },
            addGuidelinePoint(yRightBaseline, yLeftBaseline, yMinNormalData, yMaxNormalData, yMeanData)
            {
                if ('Right' === this.eyeLocation)
                {
                    if (this.testsAndParameters.rightBaseline)
                    {
                        yRightBaseline.push(this.testsAndParameters.rightBaseline.item)
                    }
                }
                if ('Left' === this.eyeLocation || this.testsAndParameters.showTogether)
                {
                    if (this.testsAndParameters.leftBaseline)
                    {
                        yLeftBaseline.push(this.testsAndParameters.leftBaseline.item)
                    }
                }
                // Only show if we have normative data
                if (this.testsAndParameters.minNormalData)
                {
                    yMinNormalData.push(this.testsAndParameters.minNormalData);
                    yMaxNormalData.push(this.testsAndParameters.maxNormalData);
                    yMeanData.push(this.testsAndParameters.meanData)
                }
            }
        }
    };
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
    .btn-map-fullscreen {
        position: absolute;
        right: 1px;
        top: 1px;
        width: 36px;
        height: 36px;
        padding: 0;
        font-size: 36px;
        line-height: 36px;
        text-align: center;
        outline: none;
    }

    .fullscreen {
        .btn-map-fullscreen {
            right: 1px;
            top: 1px;
        }
    }
</style>